'use client';
import React, { useState } from 'react';
import { X, Paperclip, CheckCircle2, Clock, AlertCircle, Upload } from 'lucide-react';
import { ObservationItem, ObservationStatus } from './followUpData';

interface Props {
  observation: ObservationItem;
  reportId: string;
  onClose: () => void;
  onUpdate: (obsId: string, updates: Partial<ObservationItem>) => void;
}

const STATUS_OPTIONS: ObservationStatus[] = ['Open', 'In Progress', 'Closed'];

const STATUS_ICONS: Record<ObservationStatus, React.ReactNode> = {
  Open: <AlertCircle size={14} className="text-red-600" />,
  'In Progress': <Clock size={14} className="text-amber-600" />,
  Closed: <CheckCircle2 size={14} className="text-green-600" />,
};

const SEVERITY_CLASS: Record<string, string> = {
  Critical: 'bg-red-100 text-red-700',
  Severe: 'bg-orange-100 text-orange-700',
  Moderate: 'bg-amber-100 text-amber-700',
  Minor: 'bg-yellow-100 text-yellow-700',
  Normal: 'bg-gray-100 text-gray-600',
};

export default function ObservationDetailPanel({ observation, onClose, onUpdate }: Props) {
  const [correctiveAction, setCorrectiveAction] = useState(observation.correctiveAction);
  const [ownerComment, setOwnerComment] = useState(observation.ownerComment);
  const [status, setStatus] = useState<ObservationStatus>(observation.status);
  const [dueDate, setDueDate] = useState(observation.dueDate);
  const [evidenceFiles, setEvidenceFiles] = useState<string[]>(observation.ownerEvidence);
  const [dirty, setDirty] = useState(false);

  const handleSave = () => {
    const updates: Partial<ObservationItem> = {
      correctiveAction,
      ownerComment,
      status,
      dueDate,
      ownerEvidence: evidenceFiles,
    };
    if (status === 'Closed' && !observation.closedDate) {
      updates.closedDate = new Date().toISOString().split('T')[0];
    }
    onUpdate(observation.id, updates);
    setDirty(false);
  };

  const handleEvidenceUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const names = files.map((f) => f.name);
    setEvidenceFiles((prev) => [...prev, ...names]);
    setDirty(true);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white w-full max-w-2xl rounded-t-2xl md:rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 bg-white z-10" style={{ borderColor: 'hsl(var(--border))' }}>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono font-bold text-primary text-sm">{observation.ref}</span>
              <span className={`px-2 py-0.5 rounded text-xs font-semibold ${SEVERITY_CLASS[observation.severity] || ''}`}>
                {observation.severity}
              </span>
              <span className="text-xs font-bold text-muted">LSD {observation.lsdScore}</span>
            </div>
            <p className="text-xs text-muted mt-0.5">Category: {observation.category}</p>
          </div>
          <button onClick={onClose} className="text-muted hover:text-foreground transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Observation Description */}
          <div>
            <p className="text-xs font-semibold text-muted mb-1 uppercase tracking-wide">Observation</p>
            <p className="text-sm leading-relaxed bg-gray-50 rounded-lg p-3">{observation.description}</p>
          </div>

          {/* Inspector Photo Evidence */}
          <div>
            <p className="text-xs font-semibold text-muted mb-2 uppercase tracking-wide">Inspector Photo Evidence</p>
            {observation.photoEvidence.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {observation.photoEvidence.map((p, i) => (
                  <div key={`pe-${i}`} className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center text-xs text-muted">
                    <Paperclip size={14} />
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted italic">No photos attached by inspector</p>
            )}
          </div>

          {/* Status */}
          <div>
            <p className="text-xs font-semibold text-muted mb-2 uppercase tracking-wide">Observation Status</p>
            <div className="flex gap-2">
              {STATUS_OPTIONS.map((s) => (
                <button
                  key={`status-${s}`}
                  onClick={() => { setStatus(s); setDirty(true); }}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-xs font-semibold transition-all ${
                    status === s
                      ? s === 'Open' ? 'bg-red-50 border-red-300 text-red-700' :
                        s === 'In Progress'? 'bg-amber-50 border-amber-300 text-amber-700' : 'bg-green-50 border-green-300 text-green-700' :'border-gray-200 text-muted hover:border-gray-300'
                  }`}
                >
                  {STATUS_ICONS[s]} {s}
                </button>
              ))}
            </div>
          </div>

          {/* Due Date */}
          <div>
            <label className="block text-xs font-semibold text-muted mb-1 uppercase tracking-wide">Due Date</label>
            <input
              type="date"
              className="input-field text-sm w-48"
              value={dueDate}
              onChange={(e) => { setDueDate(e.target.value); setDirty(true); }}
            />
          </div>

          {/* Corrective Action */}
          <div>
            <label className="block text-xs font-semibold text-muted mb-1 uppercase tracking-wide">Corrective Action Taken</label>
            <textarea
              className="input-field text-sm w-full resize-none"
              rows={3}
              placeholder="Describe the corrective action taken by the owner/manager..."
              value={correctiveAction}
              onChange={(e) => { setCorrectiveAction(e.target.value); setDirty(true); }}
            />
          </div>

          {/* Owner Comment */}
          <div>
            <label className="block text-xs font-semibold text-muted mb-1 uppercase tracking-wide">Owner Comment</label>
            <textarea
              className="input-field text-sm w-full resize-none"
              rows={2}
              placeholder="Additional comments from owner..."
              value={ownerComment}
              onChange={(e) => { setOwnerComment(e.target.value); setDirty(true); }}
            />
          </div>

          {/* Owner Evidence Upload */}
          <div>
            <p className="text-xs font-semibold text-muted mb-2 uppercase tracking-wide">Owner Evidence Attachments</p>
            <div className="space-y-2">
              {evidenceFiles.map((f, i) => (
                <div key={`ev-${i}`} className="flex items-center gap-2 bg-green-50 rounded-lg px-3 py-2">
                  <Paperclip size={13} className="text-green-600 flex-shrink-0" />
                  <span className="text-xs text-green-700 flex-1 truncate">{f}</span>
                  <button
                    onClick={() => { setEvidenceFiles((prev) => prev.filter((_, j) => j !== i)); setDirty(true); }}
                    className="text-muted hover:text-destructive transition-colors"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
              <label className="flex items-center gap-2 cursor-pointer border border-dashed border-gray-300 rounded-lg px-3 py-2 hover:border-primary hover:bg-blue-50 transition-all">
                <Upload size={13} className="text-muted" />
                <span className="text-xs text-muted">Attach evidence files (photos, documents)</span>
                <input type="file" multiple className="hidden" accept="image/*,.pdf,.doc,.docx" onChange={handleEvidenceUpload} />
              </label>
            </div>
          </div>

          {/* Closed Date */}
          {observation.closedDate && (
            <div className="bg-green-50 rounded-lg px-4 py-3 flex items-center gap-2">
              <CheckCircle2 size={14} className="text-green-600" />
              <span className="text-xs text-green-700 font-medium">Closed on {observation.closedDate}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t flex items-center justify-end gap-3 sticky bottom-0 bg-white" style={{ borderColor: 'hsl(var(--border))' }}>
          <button className="btn-secondary text-sm" onClick={onClose}>Cancel</button>
          <button
            className="btn-primary text-sm"
            onClick={handleSave}
            disabled={!dirty}
            style={{ opacity: dirty ? 1 : 0.5 }}
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}
